/// <reference path="../../../../../node_modules/@types/webpack-env/index.d.ts" />
import {
  TRANSLOCO_CONFIG,
  TRANSLOCO_MISSING_HANDLER,
  TRANSLOCO_SCOPE,
  TranslocoConfig,
  TranslocoMissingHandler,
  TranslocoMissingHandlerData,
  TranslocoService,
} from '@ngneat/transloco';
import { InjectionToken, Provider } from '@angular/core';
import { ComponentTranslations, RequireContextFn, RequiredTranslocoConfig } from '../interface';
import { TranslationLoaderService } from '../service/translation-loader';
import { MissingTranslationHelperService } from '../service/missing-translation-helper';
import _get from 'lodash/get';

// uglyhack tokens for plain values passing to factory function
export const SCOPE_TOKEN = new InjectionToken('i18n.scope');
export const TRANSLATIONS_TOKEN = new InjectionToken('i18n.translations');

export function provideTranslation(scope: string, translationsContextFn: RequireContextFn): Provider[] {
  return [
    // NOTE: `multi: true` is required cause tokens are application-wide when registered at module level
    { provide: SCOPE_TOKEN, useValue: scope, multi: true },
    { provide: TRANSLATIONS_TOKEN, useValue: translationsContextFn, multi: true },
    {
      provide: TRANSLOCO_SCOPE,
      useFactory: translationFactory,
      deps: [SCOPE_TOKEN, TRANSLATIONS_TOKEN, TranslationLoaderService, MissingTranslationHelperService],
    },
  ];
}

export function provideTranslocoConfig(config: RequiredTranslocoConfig): Provider[] {
  return [
    {
      provide: TRANSLOCO_CONFIG,
      useValue: {
        reRenderOnLangChange: true,
        prodMode: true,
        missingHandler: { useFallbackTranslation: true },
        ...config,
      } as TranslocoConfig,
    },
    {
      provide: TRANSLOCO_MISSING_HANDLER,
      useFactory: missingTranslationHelperFactory,
      deps: [MissingTranslationHelperService],
    },
  ];
}

function getTranslationsFromContext(translationsContext: __WebpackModuleApi.RequireContext): ComponentTranslations {
  return (
    translationsContext
      .keys()
      // exclude `.../.../en` duplicate cases, process only `.../.../en.json`
      // storybook can include these cases
      .filter(filePath => filePath.split('/').slice(-1)[0].includes('.'))
      .map(key => {
        const fileNameMatch = key.match(/([a-z0-9-_]+)\.json$/);
        if (!fileNameMatch) {
          throw new Error(`File ${key} doesn't match for translation`);
        }

        return { key, locale: fileNameMatch[1] };
      })
      .reduce((messages, { key, locale }) => ({ ...messages, [locale]: translationsContext(key) }), {})
  );
}

function missingTranslationHelperFactory(
  missingTranslationHelperService: MissingTranslationHelperService,
): TranslocoMissingHandler {
  return {
    handle: (key: string, data: TranslocoMissingHandlerData): string => {
      const scope = key.split('.')[0];
      const translationPath = key.split('.').slice(1).join('.');

      const isScopeLoaded = missingTranslationHelperService.isScopeLoaded(scope);
      const scopeTranslation = missingTranslationHelperService.getScopeTranslation(scope);

      const isEmptyTranslationString = _get(scopeTranslation, translationPath) === '';

      if (isEmptyTranslationString) {
        return '';
      }

      if (isScopeLoaded) {
        console.error(`Missed transloco key in ${data.activeLang} locale: ${key}`);
      }

      return key;
    },
  };
}

// using `TRANSLOCO_SCOPE` token as first component creation hook to register translation
export function translationFactory(
  scopes: string[],
  translationsContextFns: RequireContextFn[],
  translationLoaderService: TranslationLoaderService,
): null {
  scopes.forEach((scope, index) => {
    const translations = getTranslationsFromContext(translationsContextFns[index]());

    translationLoaderService.registerTranslations(scope, translations);
  });

  return null;
}

export function registerTranslationsManually(
  scope: string,
  translocoService: TranslocoService,
  context: __WebpackModuleApi.RequireContext,
): void {
  const translations = getTranslationsFromContext(context);
  for (const language of Object.keys(translations)) {
    translocoService.setTranslation({ [scope]: translations[language] }, language, { emitChange: false });
  }
}
